/**
 * Client-side checks for WEEKENDREOLINK campaign UI.
 * Server enforces eligibility — keep rules aligned with server/api-util/promoReolinkWeekend.js
 */

const PARIS_TZ = 'Europe/Paris';

export const REOLINK_PROMO_CODE = 'WEEKENDREOLINK';
export const PROMO_QUERY_PARAM = 'promoReolink';

/** SessionStorage: user completed signup flow with promo flag (show -5% on listings until consumed server-side). */
export const SESSION_PROMO_UI_KEY = 'reolinkPromoUiActive_20260417';

const calendarYearMonthDayInTimeZone = (date, timeZone) => {
  if (typeof Intl === 'undefined') return null;
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    const parts = formatter.formatToParts(date);
    const year = Number(parts.find(p => p.type === 'year')?.value);
    const month = Number(parts.find(p => p.type === 'month')?.value);
    const day = Number(parts.find(p => p.type === 'day')?.value);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    return { year, month, day };
  } catch {
    return null;
  }
};

/**
 * Campaign: 2026-04-17 00:00 – 2026-04-19 23:59:59.999 Europe/Paris
 */
export const getReolinkCampaignStartDate = () => new Date('2026-04-17T00:00:00+02:00');

export const getReolinkCampaignEndDate = () => new Date('2026-04-19T23:59:59.999+02:00');

export const isReolinkWeekendCampaignActive = () => {
  if (typeof Date === 'undefined') return false;
  const now = new Date();
  return now >= getReolinkCampaignStartDate() && now <= getReolinkCampaignEndDate();
};

/**
 * User must have signed up during the campaign window (Paris calendar dates).
 */
export const isUserSignupDuringReolinkCampaign = createdAt => {
  if (!createdAt) return false;
  const d = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (Number.isNaN(d.getTime())) return false;
  const ymd = calendarYearMonthDayInTimeZone(d, PARIS_TZ);
  if (!ymd) return false;
  if (ymd.year !== 2026 || ymd.month !== 4) return false;
  return ymd.day >= 17 && ymd.day <= 19;
};

export const normalizeReolinkBrandForPromo = raw => {
  const b = Array.isArray(raw) ? raw[0] : raw;
  if (typeof b !== 'string') return null;
  const normalized = b.trim().toLowerCase();
  return normalized === 'reolink' ? 'reolink' : null;
};

export const isReolinkSecurityCameraListing = publicData => {
  if (!publicData || typeof publicData !== 'object') return false;
  if (publicData.categoryLevel1 !== 'security-cameras') return false;
  return normalizeReolinkBrandForPromo(publicData.brand) === 'reolink';
};

/**
 * True when the user should see Reolink prices at -5% in the UI (listing cards, etc.).
 */
export const shouldShowReolinkPromoPrice = ({
  currentUser,
  hasPromoQueryFlag,
  reolinkPromoConsumed,
}) => {
  if (!isReolinkWeekendCampaignActive()) return false;
  if (!currentUser?.id) return false;
  if (!isUserSignupDuringReolinkCampaign(currentUser.attributes?.createdAt)) return false;
  if (reolinkPromoConsumed) return false;
  if (typeof window !== 'undefined') {
    try {
      if (window.sessionStorage.getItem(SESSION_PROMO_UI_KEY) === '1') return true;
    } catch {
      /* ignore */
    }
  }
  return !!hasPromoQueryFlag;
};

/** Call when landing with ?promoReolink=1 so promo prices persist across navigation. */
export const persistReolinkPromoUiSession = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SESSION_PROMO_UI_KEY, '1');
  } catch {
    /* ignore */
  }
};
